'use client';

import * as React from 'react';

import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Calendar } from '@/components/ui/calendar';
import { Field, FieldLabel } from '@/components/ui/field';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/**
 * Class preset that sizes a DatePickerInput to match the compact (h-8) inputs
 * used across the modern modules. Apply via the component's `className`.
 */
export const DATE_PICKER_COMPACT =
  '[&_[data-slot=input-group]]:h-8 [&_[data-slot=input-group]]:rounded-lg [&_[data-slot=input-group]]:border-input [&_[data-slot=input-group]]:shadow-none [&_[data-slot=input-group-addon]]:py-0 [&_[data-slot=input-group-addon]]:h-8 [&_input]:h-8 [&_input]:px-2.5 [&_input]:py-1 [&_input]:text-sm [&_input]:font-normal [&_input]:text-foreground';

function formatDate(date: Date | undefined) {
  if (!date || isNaN(date.getTime())) {
    return '';
  }

  return format(date, 'MM/dd/yyyy');
}

function isValidDate(date: Date | undefined) {
  if (!date) {
    return false;
  }
  return !Number.isNaN(date.getTime());
}

type DatePickerInputProps = {
  label?: string;
  initialDate?: Date;
  locale?: React.ComponentProps<typeof Calendar>['locale'];
  id?: string;
  className?: string;
  container?: React.ComponentProps<typeof PopoverContent>['container'];
  onChange?: (date: Date | null) => void;
  placeholder?: string;
  disabled?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
} & Omit<React.ComponentProps<typeof InputGroupInput>, 'onChange'>;

export function DatePickerInput({
  label,
  initialDate,
  locale,
  id = 'date-required',
  className,
  container,
  onChange,
  placeholder = '',
  disabled = false,
  inputRef,
  ...rest
}: DatePickerInputProps) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [date, setDate] = React.useState<Date | undefined>(initialDate);
  const [displayValue, setDisplayValue] = React.useState(
    formatDate(initialDate),
  );

  return (
    <Field className={cn('w-full', className)}>
      {label && <FieldLabel htmlFor={id}>{label}</FieldLabel>}
      <InputGroup className="h-full rounded-md">
        <InputGroupInput
          ref={inputRef}
          id={id}
          value={displayValue}
          placeholder={placeholder}
          className="h-full px-4 py-3"
          disabled={disabled}
          onChange={(e) => {
            const dateStr = e.target.value;
            setDisplayValue(dateStr);
            const parsed = new Date(dateStr);
            if (isValidDate(parsed)) {
              setDate(parsed);
              onChange?.(parsed);
            } else if (!dateStr.trim()) {
              setDate(undefined);
              onChange?.(null);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setOpen(true);
            }
          }}
          {...rest}
        />
        <InputGroupAddon align="inline-end" className="pr-1">
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger
              render={
                <InputGroupButton
                  id={`${id}-picker`}
                  variant="ghost"
                  size="icon-xs"
                  className="mx-1 rounded-md"
                  aria-label={t('common.selectDate')}
                  disabled={disabled}
                >
                  <CalendarIcon size={20} />
                  <span className="sr-only">{t('common.selectDate')}</span>
                </InputGroupButton>
              }
            />
            <PopoverContent
              className="w-auto overflow-hidden p-0"
              align="end"
              alignOffset={-8}
              sideOffset={10}
              container={container}
              onClick={(e) => e.stopPropagation()}
            >
              <Calendar
                mode="single"
                locale={locale}
                disabled={disabled}
                captionLayout="dropdown"
                selected={date}
                defaultMonth={date}
                endMonth={new Date(new Date().getFullYear() + 5, 11)}
                onSelect={(selected) => {
                  setDate(selected);
                  setDisplayValue(formatDate(selected));
                  onChange?.(selected ?? null);
                  setOpen(false);
                }}
              />
            </PopoverContent>
          </Popover>
        </InputGroupAddon>
      </InputGroup>
    </Field>
  );
}
